import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings } from 'lucide-react';
import { ProjectSettings } from '@/types';
import { useState } from 'react';

interface ProjectSettingsDialogProps {
  settings: ProjectSettings;
  onSave: (settings: ProjectSettings) => void;
}

export default function ProjectSettingsDialog({ settings, onSave }: ProjectSettingsDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState(settings);
  const [nicknameError, setNicknameError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate nickname (no spaces, only alphanumeric, hyphens, and underscores)
    if (!/^[a-zA-Z0-9-_]+$/.test(formData.nickname)) {
      setNicknameError('Nickname can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    onSave(formData);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Project Settings</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Project Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="My FiveM UI"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="author">Author</Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              placeholder="Your Name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="nickname">
              Project Nickname
              <span className="text-sm text-muted-foreground ml-2">
                (used for file names and events)
              </span>
            </Label>
            <Input
              id="nickname"
              value={formData.nickname}
              onChange={(e) => {
                setFormData({ ...formData, nickname: e.target.value });
                setNicknameError('');
              }}
              placeholder="my_fivem_ui"
              required
            />
            {nicknameError && (
              <p className="text-sm text-destructive">{nicknameError}</p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Save Settings</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}